/**
 * Writing mail: a new email, or a forward of a message in a thread. Replies are written inline in
 * the thread (ThreadView); this is everything that starts a conversation with someone new.
 *
 * The inbox signature is added by the server on send (unless already in the text), so it is not
 * pasted into the box here — the hint under the body says so.
 */

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Loader2, Paperclip, Send, X } from 'lucide-react'
import { useCompose, useForward } from '../hooks/useInbox'

const MAX_TOTAL = 18 * 1024 * 1024

/** File chips + an "Attach" button. Keeps the total under the server's 18 MB budget. */
export function AttachmentPicker({ files, onChange }: { files: File[]; onChange: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const add = (list: FileList | null) => {
    if (!list) return
    const next = files.concat(Array.from(list)).slice(0, 10)
    const total = next.reduce((n, f) => n + f.size, 0)
    if (total > MAX_TOTAL) {
      setError('Attachments can be 18 MB in total.')
      return
    }
    setError(null)
    onChange(next)
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          add(e.target.files)
          e.target.value = ''
        }}
      />
      <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => input.current?.click()}>
        <Paperclip className="mr-1.5 h-3.5 w-3.5" /> Attach
      </Button>
      {files.map((f, i) => (
        <span key={`${f.name}-${i}`} className="flex max-w-[220px] items-center gap-1 rounded-md border px-2 py-1 text-xs">
          <span className="truncate">{f.name}</span>
          <button
            type="button"
            aria-label={`Remove ${f.name}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onChange(files.filter((_, j) => j !== i))}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  )
}

export type ComposeMode =
  | { kind: 'new'; to?: string }
  | { kind: 'forward'; threadId: number; messageId?: number; subject: string | null }

interface Props {
  mode: ComposeMode | null
  onClose: () => void
  signature: string
  fromAddress: string
}

export function ComposeDialog({ mode, onClose, signature, fromAddress }: Props) {
  const compose = useCompose()
  const forward = useForward()
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [showCopies, setShowCopies] = useState(false)
  const [subject, setSubject] = useState('')
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])

  // Fresh form each time the window opens.
  useEffect(() => {
    if (!mode) return
    setTo(mode.kind === 'new' ? mode.to || '' : '')
    setCc('')
    setBcc('')
    setShowCopies(false)
    setSubject(mode.kind === 'forward' ? `Fwd: ${(mode.subject || '').replace(/^(fwd?|re):\s*/i, '')}` : '')
    setText('')
    setFiles([])
  }, [mode])

  const isForward = mode?.kind === 'forward'
  const pending = compose.isPending || forward.isPending
  const canSend = to.trim() && (isForward || (subject.trim() && text.trim())) && !pending

  const send = () => {
    if (!mode) return
    const mail = { to, cc, bcc, subject, text, files }
    const done = { onSuccess: onClose }
    if (mode.kind === 'forward') forward.mutate({ id: mode.threadId, mail: { ...mail, messageId: mode.messageId } }, done)
    else compose.mutate(mail, done)
  }

  return (
    <Dialog open={!!mode} onOpenChange={(open) => !open && !pending && onClose()}>
      <DialogContent className="max-w-2xl gap-3">
        <DialogHeader>
          <DialogTitle>{isForward ? 'Forward' : 'New email'}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground">From {fromAddress}</div>
        <div className="flex items-center gap-2">
          <Input placeholder="To" value={to} onChange={(e) => setTo(e.target.value)} autoFocus />
          {!showCopies && (
            <Button variant="ghost" size="sm" onClick={() => setShowCopies(true)}>
              Cc/Bcc
            </Button>
          )}
        </div>
        {showCopies && (
          <>
            <Input placeholder="Cc" value={cc} onChange={(e) => setCc(e.target.value)} />
            <Input placeholder="Bcc" value={bcc} onChange={(e) => setBcc(e.target.value)} />
          </>
        )}
        <Input
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={isForward}
        />
        <Textarea
          rows={10}
          placeholder={isForward ? 'Add a note (optional) — the original message is included below it.' : 'Write your email…'}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Separate addresses with commas.{signature.trim() ? ' Your signature is added when you send.' : ''}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AttachmentPicker files={files} onChange={setFiles} />
          <Button onClick={send} disabled={!canSend}>
            {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            {isForward ? 'Forward' : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
